import type { FormError, FormField, FormValidator } from '@workwave-tidal/form-fairy'

interface Validation<
  FORM_FIELDS extends Record<string, FormField>,
  FORM_ERRORS extends FormError<keyof FORM_FIELDS>,
> {
  id: string
  validator: FormValidator<FORM_FIELDS, FORM_ERRORS>
  fields?: keyof FORM_FIELDS
}

function isSimpleRepeatedOrSequentialPattern(normalizedValue: string) {
  // the password is a unit repeated, of any length, a partial unit at the end still counts:
  // ex:- aaaaaa, 111111, 121212121212121, abcabcab, passwordpassword
  // the unit has to fit at least twice, so it can be at most half of the password
  const maxCheckedUnitLength = Math.floor(normalizedValue.length / 2)

  for (let unitLength = 1; unitLength <= maxCheckedUnitLength; unitLength++) {
    let repeatsUnit = true

    for (let i = unitLength; i < normalizedValue.length; i++) {
      if (normalizedValue[i] !== normalizedValue[i % unitLength]) {
        repeatsUnit = false
        break
      }
    }

    if (repeatsUnit) return true
  }

  // the whole password is a run of consecutive characters:ex:- 123456789, abcdef, 987654
  const step = normalizedValue.charCodeAt(1) - normalizedValue.charCodeAt(0)

  if (step !== 1 && step !== -1) return false

  for (let i = 2; i < normalizedValue.length; i++) {
    if (normalizedValue.charCodeAt(i) - normalizedValue.charCodeAt(i - 1) !== step) return false
  }

  return true
}

export function getStandardPasswordValidations<
  FORM_FIELDS extends Record<string, FormField>,
  FORM_ERRORS extends FormError<keyof FORM_FIELDS>,
>(field: keyof FORM_FIELDS, username?: string, reservedTerms: string[] = []) {
  const validatePassword: Validation<FORM_FIELDS, FORM_ERRORS> = {
    id: `${field as string}_validateStandardPassword`,
    validator: formApi => {
      const { value } = formApi.getField(field)

      if (typeof value !== 'string')
        throw new Error('The provided password field should be a string')

      const tooShortID = `${field as string}_too_short`
      const tooShort = value.trim().length < 15

      const tooShortError = {
        id: tooShortID,
        field,
        message: 'Password must be at least 15 characters.',
        priority: 30,
      }

      const normalizedValue = value.toLowerCase()

      const matchesUsernameID = `${field as string}_excludes_username`
      const normalizedUsername = username?.trim().toLowerCase()
      const matchesUsername =
        !!normalizedUsername && value.trim().toLowerCase() === normalizedUsername

      const matchesUsernameError = {
        id: matchesUsernameID,
        field,
        message: 'Should not match the username.',
        priority: 5,
      }

      const containsReservedTermID = `${field as string}_excludes_reserved_terms`
      const containsReservedTerm = reservedTerms.some(term => {
        const normalizedTerm = term.trim().toLowerCase()

        return normalizedTerm.length > 0 && normalizedValue.includes(normalizedTerm)
      })

      const containsReservedTermError = {
        id: containsReservedTermID,
        field,
        message: 'Should not contain reserved terms.',
        priority: 1,
      }

      const repeatedPatternID = `${field as string}_excludes_repeated_patterns`
      const trimmedValue = value.trim()
      const repeatedPattern =
        trimmedValue.length > 1 && isSimpleRepeatedOrSequentialPattern(trimmedValue.toLowerCase())

      const repeatedPatternError = {
        id: repeatedPatternID,
        field,
        message: 'Should not be a simple repeated or sequential pattern.',
        priority: 28,
      }

      return {
        [tooShortID]: tooShort ? tooShortError : null,
        [repeatedPatternID]: repeatedPattern ? repeatedPatternError : null,
        [matchesUsernameID]: matchesUsername ? matchesUsernameError : null,
        [containsReservedTermID]: containsReservedTerm ? containsReservedTermError : null,
      }
    },
    fields: field,
  }

  return [validatePassword]
}
